// Page entry for `/russia-rates`. Full-bleed shell mirroring
// `RequestsListPage` (china), single-select status + type chip groups,
// optional manager picker on the right side of the chip strip, a
// `q` search input, and a desktop table / mobile cards switch via
// `useNarrow()`.
//
// Filter state is owned by `useRussiaRequestsFilters` — URL is the
// single writable surface (#942). The wire query is suppressed while
// any hydrated `managerId` is being validated against
// `/russia-requests/manager-options`. Cursor-paginated infinite list
// (#943) keeps pages 2+ behind an explicit `Load more` button — no
// auto-load on scroll.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router';
import {
  RequestStatus,
  RussiaRequestType,
  isStatusHiddenFromLogist,
  type RussiaRequest,
} from '@magimp/shared';

import { Button, FilterPanel, Input, useFilterDisclosure, useNarrow } from '../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { canCreateRussiaRequestManual } from '../role.js';
import { detectRoleKind, STATUS_LABEL_KEYS } from '../../requests/display/index.js';
import { detectStatusFilterRole, isLogistStatusFilterRole } from '../../../shared/index.js';
import { useRussiaRequestManagerOptions, useRussiaRequestsInfinite } from '../queries.js';
import { entityKey, useNotificationsByEntity } from '../../notifications/index.js';
import { RUSSIA_SEARCH_MAX_CHARS, useRussiaRequestsFilters } from '../useRussiaRequestsFilters.js';
import { RussiaRequestMobileCard } from './RussiaRequestMobileCard.js';
import { RussiaRequestsFilterChip } from './RussiaRequestsFilterChip.js';
import { RussiaRequestsListPageHeader } from './RussiaRequestsListPageHeader.js';
import { RussiaRequestsListTable } from './RussiaRequestsListTable.js';

const STATUS_CHIPS_FULL: readonly RequestStatus[] = [
  RequestStatus.DRAFT,
  RequestStatus.IN_WORK,
  RequestStatus.RETURN,
  RequestStatus.PROCESSED,
  // WHY (#1837): POSTPONED is active/non-terminal and mirrors PROCESSED.
  //   A parked request needs a filter chip so a manager can isolate the
  //   parked set; without a chip a parked row is only visible in the
  //   unfiltered list. NOT in LOGIST_HIDDEN_STATUSES, so it carries into
  //   STATUS_CHIPS_LOGIST too.
  RequestStatus.POSTPONED,
  RequestStatus.SUCCESS,
  RequestStatus.REFUSED,
  RequestStatus.CLOSED,
  RequestStatus.ARCHIVE,
];
// INVARIANT (#1159 PR5): hidden statuses are removed from the chip set
//   for logist_ru actors so the page never renders a DRAFT or RETURN
//   chip whose count would be a misleading zero.
const STATUS_CHIPS_LOGIST: readonly RequestStatus[] = STATUS_CHIPS_FULL.filter(
  (s) => !isStatusHiddenFromLogist(s),
);

const TYPE_CHIPS: readonly RussiaRequestType[] = [
  RussiaRequestType.LEAD,
  RussiaRequestType.WAREHOUSE,
  RussiaRequestType.IN_TRANSIT,
  RussiaRequestType.URGENT,
];

const TYPE_LABEL_KEYS = {
  [RussiaRequestType.LEAD]: 'types.lead',
  [RussiaRequestType.WAREHOUSE]: 'types.warehouse',
  [RussiaRequestType.IN_TRANSIT]: 'types.inTransit',
  [RussiaRequestType.URGENT]: 'types.urgent',
} as const satisfies Record<RussiaRequestType, string>;

export function RussiaRequestsListPage() {
  const { t, i18n } = useTranslation(['russiaRequests', 'requests']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  // WHY: route-gate tests construct fake `user` objects without a
  //   `.role` field; the original list page did not surface a role
  //   subtitle and never tripped `detectRoleKind`. Guarding here keeps
  //   the new subtitle while preserving the tests' minimal user shape.
  const role = user && user.role ? detectRoleKind(user) : null;
  // INVARIANT (#1159 PR5): the hidden-status filter axis is separate
  //   from `RoleKind` because the latter only knows about China; here
  //   logist_ru is the relevant role and the helper covers it.
  const statusFilterRole = detectStatusFilterRole(user && user.role ? user : null);
  const STATUS_CHIPS = isLogistStatusFilterRole(statusFilterRole)
    ? STATUS_CHIPS_LOGIST
    : STATUS_CHIPS_FULL;
  const canCreate = canCreateRussiaRequestManual(permissions);
  // WHY (#706, Codex F1): `permissions` is `readonly PermissionCode[] | 'ALL'`.
  //   A plain `includes` returns false for the string sentinel `'ALL'` and
  //   would hide the picker from admins. Route the check through the
  //   `hasPermission` helper which short-circuits on `'ALL'`.
  const canFilterByManager = hasPermission(permissions, 'russiaRequests.scope.all');
  const narrow = useNarrow();
  const navigate = useNavigate();
  // WHY (#1507): chip group is collapsed by default behind the header
  //   "Statuses" toggle; the id wires the toggle's `aria-controls` to the
  //   always-mounted chip wrapper below.
  const [chipsOpen, setChipsOpen] = useState(false);
  const chipsGroupId = 'russia-requests-chip-group';

  // WHY (#1527): the free-text search collapses behind the header
  //   "Filters" toggle, closed by default on desktop AND mobile; the id
  //   wires the toggle's `aria-controls` to the always-mounted panel.
  const searchDisclosure = useFilterDisclosure();

  const filters = useRussiaRequestsFilters({ canPickManager: canFilterByManager });

  const filtersActiveCount = filters.q.trim().length > 0 ? 1 : 0;

  const query = useRussiaRequestsInfinite(filters.wireFilters ?? {}, {
    enabled: filters.wireFilters !== null,
  });
  const managerOptionsQuery = useRussiaRequestManagerOptions({ enabled: canFilterByManager });

  const openRow = (id: string) => {
    navigate(`/russia-rates/${encodeURIComponent(id)}`);
  };

  // Flatten loaded pages in render order. The wire is canonical for
  // ordering — no client-side sort.
  const rows = useMemo<readonly RussiaRequest[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data?.pages],
  );

  // INVARIANT (#1072): RF list reuses the shared notifications query
  //   keyed by entityType=RUSSIA_REQUEST. The lookup is a Map miss for
  //   rows with no unread aggregates, so the dot disappears as soon as
  //   `useMarkNotificationViewedByTarget` zeroes the entry.
  const { byEntity: notificationsByEntity } = useNotificationsByEntity();
  const notificationCountFor = useCallback(
    (id: string): number =>
      notificationsByEntity.get(entityKey('RUSSIA_REQUEST', id))?.totalUnreadAggregates ?? 0,
    [notificationsByEntity],
  );

  // INVARIANT: counts describe the LOADED slice (visibility-scoped wire
  //   response). With a status filter active, only that status is in the
  //   slice — so chips for other statuses carry no honest count and stay
  //   uncounted. The same rule applies to type chips.
  const statusCounts = useMemo<Record<string, number | undefined>>(() => {
    if (filters.status !== null) return {};
    const out: Record<string, number | undefined> = { all: rows.length };
    for (const s of STATUS_CHIPS) {
      out[s] = rows.filter((r) => r.status === s).length;
    }
    return out;
  }, [rows, filters.status]);

  const typeCounts = useMemo<Record<string, number | undefined>>(() => {
    if (filters.type !== null) return {};
    const out: Record<string, number | undefined> = { all: rows.length };
    for (const tp of TYPE_CHIPS) {
      out[tp] = rows.filter((r) => r.type === tp).length;
    }
    return out;
  }, [rows, filters.type]);

  // While `wireFilters === null` the list query is disabled — show the
  // loading branch so the page does not flash an empty slate.
  const showLoading = filters.wireFilters === null || query.isLoading;

  // WHY (#1507): badge count surfaces active chip filters while collapsed.
  const activeChipCount = (filters.status !== null ? 1 : 0) + (filters.type !== null ? 1 : 0);

  return (
    <div
      data-testid="russia-requests-list-page"
      style={{
        position: 'relative',
        margin: -16,
        height: 'calc(100% + 32px)',
        width: 'calc(100% + 32px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-0)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <RussiaRequestsListPageHeader
        role={role}
        narrow={narrow}
        canCreate={canCreate}
        chipsOpen={chipsOpen}
        onToggleChips={() => setChipsOpen((v) => !v)}
        activeChipCount={activeChipCount}
        chipsGroupId={chipsGroupId}
        filtersOpen={searchDisclosure.open}
        onToggleFilters={searchDisclosure.toggle}
        filtersActiveCount={filtersActiveCount}
        filtersPanelId={searchDisclosure.panelId}
        onCreateSimple={() => navigate('/russia-rates/new?kind=SIMPLE')}
        onCreateComplex={() => navigate('/russia-rates/new?kind=COMPLEX')}
      />

      {/* WHY (#1527): the search moved into the "Filters" panel below, so
          when chips are collapsed and no manager select shows, the strip
          would render an empty bar. Collapse it via `display:'none'` (keep
          it mounted so `russia-requests-chip-group` stays in the DOM for
          the "Statuses" `aria-controls`). */}
      <div
        data-testid="russia-requests-chip-strip"
        style={{
          padding: '10px 24px',
          display: chipsOpen || canFilterByManager ? 'flex' : 'none',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
          flex: 'none',
        }}
      >
        {/* WHY (#1507): chip group collapses by default behind the header
            toggle. The wrapper stays mounted in both states so
            `aria-controls` always resolves; `display:'contents'` when open
            keeps the expanded flex layout byte-for-byte identical. */}
        <div id={chipsGroupId} style={{ display: chipsOpen ? 'contents' : 'none' }}>
          <RussiaRequestsFilterChip
            chipId="status-all"
            label={t('russiaRequests:filters.all')}
            count={statusCounts['all']}
            active={filters.status === null}
            onClick={() => filters.setStatus(null)}
          />
          {STATUS_CHIPS.map((s) => (
            <RussiaRequestsFilterChip
              key={s}
              chipId={`status-${s}`}
              label={t(`requests:${STATUS_LABEL_KEYS[s]}`)}
              count={statusCounts[s]}
              active={filters.status === s}
              onClick={() => filters.setStatus(filters.status === s ? null : s)}
            />
          ))}
          <div
            aria-hidden
            style={{
              width: 1,
              height: 20,
              background: 'var(--border-1)',
              margin: '0 4px',
              flex: 'none',
            }}
          />
          <RussiaRequestsFilterChip
            chipId="type-all"
            label={t('russiaRequests:filters.all')}
            count={typeCounts['all']}
            active={filters.type === null}
            onClick={() => filters.setType(null)}
          />
          {TYPE_CHIPS.map((tp) => (
            <RussiaRequestsFilterChip
              key={tp}
              chipId={`type-${tp}`}
              label={t(`russiaRequests:${TYPE_LABEL_KEYS[tp]}`)}
              count={typeCounts[tp]}
              active={filters.type === tp}
              onClick={() => filters.setType(filters.type === tp ? null : tp)}
            />
          ))}
        </div>
        <div style={{ flex: 1 }} />
        {canFilterByManager && (
          <select
            data-testid="russia-requests-filter-manager"
            value={filters.managerId}
            onChange={(e) => filters.setManagerId(e.target.value)}
            style={{
              height: 28,
              padding: '0 8px',
              fontSize: 12,
              borderRadius: 'var(--r-1)',
              border: '1px solid var(--border-1)',
              background: 'var(--bg-1)',
              color: 'var(--fg-1)',
            }}
          >
            <option value="">{t('russiaRequests:filters.manager')}</option>
            {managerOptionsQuery.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <FilterPanel open={searchDisclosure.open} id={searchDisclosure.panelId}>
        <div style={{ width: narrow ? '100%' : 260 }}>
          <Input
            icon="search"
            ariaLabel={t('russiaRequests:list.searchPlaceholder')}
            placeholder={t('russiaRequests:list.searchPlaceholder')}
            value={filters.q}
            onChange={filters.setQ}
            maxLength={RUSSIA_SEARCH_MAX_CHARS}
          />
        </div>
      </FilterPanel>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: narrow ? 12 : 0,
          background: 'var(--bg-0)',
        }}
      >
        {showLoading ? (
          <p
            data-testid="russia-requests-loading"
            style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}
          >
            {t('russiaRequests:loading')}
          </p>
        ) : query.isError ? (
          <p
            data-testid="russia-requests-error"
            role="alert"
            style={{ padding: 24, color: 'var(--status-error-fg)', fontSize: 13 }}
          >
            {t('russiaRequests:error')}
          </p>
        ) : rows.length === 0 ? (
          <p
            data-testid="russia-requests-empty"
            style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}
          >
            {t('russiaRequests:empty')}
          </p>
        ) : (
          <>
            {narrow ? (
              <div
                data-testid="russia-requests-mobile-list"
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {rows.map((row) => (
                  <RussiaRequestMobileCard
                    key={row.id}
                    row={row}
                    language={i18n.language}
                    onOpen={() => openRow(row.id)}
                    notificationCount={notificationCountFor(row.id)}
                  />
                ))}
              </div>
            ) : (
              <RussiaRequestsListTable
                rows={rows}
                language={i18n.language}
                onOpenRow={openRow}
                notificationCountFor={notificationCountFor}
              />
            )}
            {query.hasNextPage && (
              <div
                style={{
                  padding: '14px 24px 22px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button
                  kind="secondary"
                  size="sm"
                  data-testid="russia-requests-load-more"
                  disabled={query.isFetchingNextPage}
                  onClick={() => {
                    void query.fetchNextPage();
                  }}
                >
                  {query.isFetchingNextPage
                    ? t('russiaRequests:list.loadingMore')
                    : t('russiaRequests:list.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* `Outlet` mounts `RussiaRequestDrawer` when the URL matches
          `/russia-rates/:id`, so the list stays mounted underneath. */}
      <Outlet />
    </div>
  );
}
